'use client';

import { c as _c } from "react/compiler-runtime";
import { DARK_PATTERN_SOURCE_LINK_PROMPT, isDarkPatternFactiii } from '@shared/all/domains/darkPatterns';
import { type FactiiiTag, type PostFactiiiReference } from '@shared/all/domains/factiiis';
import { ChevronDown, ChevronRight, PlusCircle, XCircle } from 'lucide-react';
import { useState } from 'react';
import Avatar from '../../common/Avatar';
import ScrollableText from '../../UI/ScrollableText';
import { SourceSearch } from './SourceSearch';
const convertPostFactiiiReferenceToFactiiiTag = (postFactiiiReference: PostFactiiiReference): FactiiiTag['postFactiiis'][0]['factiiiReferences'] => {
  return [{
    data: postFactiiiReference.data ?? null,
    spaceTimes: [],
    uploads: [],
    factiii: {
      id: postFactiiiReference.factiii.id,
      name: postFactiiiReference.factiii.name,
      slug: postFactiiiReference.factiii.slug,
      avatarKey: postFactiiiReference.factiii.avatarKey
    }
  }];
};
type Props = {
  tag: FactiiiTag;
  myPostFactiiiCounts: FactiiiTag[];
  setMyPostFactiiiCounts: (myPostFactiiiCounts: FactiiiTag[]) => void;
};
export const SourceRequiredFactiii: React.FC<Props> = t0 => {
  const $ = _c(31);
  const {
    tag,
    myPostFactiiiCounts,
    setMyPostFactiiiCounts
  } = t0;
  const [postsExpanded, setPostsExpanded] = useState(false);
  const [addPostModalOpened, setAddPostModalOpened] = useState(false);
  let t1;
  if ($[0] !== myPostFactiiiCounts || $[1] !== setMyPostFactiiiCounts || $[2] !== tag) {
    t1 = factiii => {
      const newTag = {
        ...tag,
        postFactiiis: [{
          ...tag.postFactiiis[0],
          factiiiReferences: [...convertPostFactiiiReferenceToFactiiiTag(factiii), ...tag.postFactiiis[0].factiiiReferences]
        }, ...tag.postFactiiis.slice(1)]
      };
      setMyPostFactiiiCounts(myPostFactiiiCounts.map(f => f.factiiiId === newTag.factiiiId ? newTag : f));
    };
    $[0] = myPostFactiiiCounts;
    $[1] = setMyPostFactiiiCounts;
    $[2] = tag;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const addHandler = t1;
  let t2;
  if ($[4] !== myPostFactiiiCounts || $[5] !== setMyPostFactiiiCounts || $[6] !== tag) {
    t2 = factiii_0 => {
      const postFactiiiReferences = tag.postFactiiis[0].factiiiReferences?.filter(postFactiii => postFactiii.factiii.id !== factiii_0.factiii.id);
      const updatedTag = {
        ...tag,
        postFactiiis: [{
          ...tag.postFactiiis[0],
          factiiiReferences: postFactiiiReferences
        }, ...tag.postFactiiis.slice(1)]
      };
      setMyPostFactiiiCounts(myPostFactiiiCounts.map(f_0 => f_0.factiiiId === tag.factiiiId ? updatedTag : f_0));
    };
    $[4] = myPostFactiiiCounts;
    $[5] = setMyPostFactiiiCounts;
    $[6] = tag;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const removeReference = t2;
  let t3;
  if ($[8] !== postsExpanded || $[9] !== removeReference || $[10] !== tag.factiiiId || $[11] !== tag.postFactiiis[0].factiiiReferences) {
    const sources = tag.postFactiiis[0].factiiiReferences.map(_temp);
    const t4 = `flex flex-row items-center rounded-md border-2 border-ds-border-standard p-4 ${postsExpanded ? " rounded-b-none" : ""} `;
    let t5;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = () => setPostsExpanded(_temp3);
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    let t6;
    if ($[14] !== postsExpanded) {
      t6 = <button className="mr-2 p-1 hover:text-primary" onClick={t5}>{postsExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</button>;
      $[14] = postsExpanded;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    const t7 = tag.postFactiiis[0].factiiiReferences?.length || 0;
    let t8;
    if ($[16] !== t7) {
      t8 = <p>{t7} Sources Added</p>;
      $[16] = t7;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = () => setAddPostModalOpened(true);
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <div onClick={t9} className="ml-auto"><PlusCircle /></div>;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    let t11;
    if ($[20] !== t4 || $[21] !== t6 || $[22] !== t8) {
      t11 = <div className={t4}>{t6}{t8}{t10}</div>;
      $[20] = t4;
      $[21] = t6;
      $[22] = t8;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    t3 = <div onClick={_temp2} key={tag.factiiiId} className="mt-2 rounded-md">{t11}{postsExpanded && <div className="space-y-5 rounded-md rounded-t-none border-2 border-t-0 border-ds-border-standard p-2 transition-all duration-300 ease-in-out">{!sources || sources.length === 0 ? <p className="text-center text-sm text-textSecondary/70">No sources added</p> : <>{sources.map(source => <div key={source.factiiiId} className="flex cursor-pointer space-x-2 rounded-md border border-ds-border-standard p-2"><Avatar spaceAvatarKey={source.factiii.avatarKey ?? ""} size={40} /><div className="min-w-0 flex-1 space-y-2"><div className="flex flex-row space-x-2"><p>{source.factiii.name}</p><p className="flex-1 text-textSecondary/70">{`f/${source.factiii.slug}`}</p><div onClick={() => removeReference(source)} className="flex flex-1 items-center justify-end"><XCircle color="red" /></div></div><div className="flex min-w-0 flex-row space-x-2"><ScrollableText className="text-textSecondary/70">{source.data}</ScrollableText></div></div></div>)}</>}</div>}</div>;
    $[8] = postsExpanded;
    $[9] = removeReference;
    $[10] = tag.factiiiId;
    $[11] = tag.postFactiiis[0].factiiiReferences;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[24] !== addHandler || $[25] !== addPostModalOpened || $[26] !== tag) {
    t4 = addPostModalOpened && <SourceSearch urlLabel={isDarkPatternFactiii(tag) ? DARK_PATTERN_SOURCE_LINK_PROMPT : undefined} callback={factiii_1 => {
      if (factiii_1) {
        addHandler(factiii_1);
      }
      return setAddPostModalOpened(false);
    }} />;
    $[24] = addHandler;
    $[25] = addPostModalOpened;
    $[26] = tag;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  let t5;
  if ($[28] !== t3 || $[29] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  return t5;
};
function _temp(ref) {
  return {
    data: ref.data ?? null,
    spaceTimes: ref.spaceTimes ?? [],
    uploads: ref.uploads ?? [],
    factiii: {
      id: ref.factiii?.id,
      name: ref.factiii?.name,
      slug: ref.factiii?.slug,
      avatarKey: ref.factiii?.avatarKey ?? null,
      requirements: []
    },
    factiiiId: ref.factiii?.id
  };
}
function _temp2(e) {
  return e.stopPropagation();
}
function _temp3(prev) {
  return !prev;
}